import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { QRCode } from 'antd';
// import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { BASE_IMG_URL, getUserLevelBadge, livePhotoImage, photographyVideoImage } from '@images/imageUrls';
import { isVideoWork } from '@utils/photographyVideo';
import { getPhotographyPosterImage } from '@utils/photographyPoster';
import Toast from '@components/Toast';
import Avatar from '@components/avatar';
import { copy } from '@utils/copy';
import styles from './index.module.scss';
import { px2rem } from '@utils/px2rem';

const LIVE_ICON_URL = livePhotoImage.liveIcon;

const posterImageUrls = {
    background: `${BASE_IMG_URL}/photography/backkou.png`,
    defaultAvatar: `${BASE_IMG_URL}/common/avatar.png`,
    officialBlue: `${BASE_IMG_URL}/common/official2.png`,
    officialGold: `${BASE_IMG_URL}/common/official1.png`,
    qrLogo: photographyVideoImage.qrLogo,
    videoPlay: photographyVideoImage.play,
};

const convertToBase64 = (url: string) => {
    return new Promise<string>((resolve, reject) => {
        if (!url) {
            resolve('');
            return;
        }
        const image = new window.Image();
        image.crossOrigin = 'Anonymous';
        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas context is unavailable'));
                return;
            }
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            ctx.drawImage(image, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        image.onerror = reject;
        // 避免复用不含 CORS 响应头的 CDN 缓存；签名地址保持原样。
        const hashIndex = url.indexOf('#');
        const baseUrl = hashIndex < 0 ? url : url.slice(0, hashIndex);
        const hash = hashIndex < 0 ? '' : url.slice(hashIndex);
        image.src = /^(?:data|blob):/i.test(url) || /[?&](?:Signature|x-oss-signature)=/i.test(url)
            ? url
            : `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}poster=${Date.now()}${hash}`;
    });
};

interface SharePosterPopupProps {
    visible: boolean;
    workData?: any;
    photographyDetail?: any;
    shareUrl: string;
    topVoteCount?: number;
    onClose?: () => void;
}

const SharePosterPopup: React.FC<SharePosterPopupProps> = ({
    visible,
    workData = {},
    photographyDetail = {},
    shareUrl,
    topVoteCount = 0,
    onClose,
}) => {
    const videoWork = isVideoWork(workData);
    const originalWorkImage = getPhotographyPosterImage(workData?.image || '');
    const [actionSheetVisible, setActionSheetVisible] = useState(false);
    const avatarUrl = workData?.user?.avatar || posterImageUrls.defaultAvatar;
    const titleMark = Number(workData?.user?.titleMark);
    const officialBadge = titleMark === 1 || titleMark === 2;
    const badgeUrl = titleMark === 2 ? posterImageUrls.officialGold
        : titleMark === 1 ? posterImageUrls.officialBlue : getUserLevelBadge(workData?.user?.level);
    const badgeLabel = titleMark === 2 ? '黑V' : titleMark === 1 ? '蓝V' : `V${workData?.user?.level}`;
    const assetKey = JSON.stringify([workData?.id, originalWorkImage, avatarUrl, videoWork, badgeUrl]);
    const [preparedImages, setPreparedImages] = useState<{ key: string; images: Record<string, string> } | null>(null);
    // 缓存只能用于当前作品；新图未转换完成时直接预览当前原图。
    const base64Images = preparedImages?.key === assetKey ? preparedImages.images : {};
    const base64ImagesLoaded = visible && preparedImages?.key === assetKey;
    const [imageDimensions, setImageDimensions] = useState({ source: '', ratio: 1 });
    const workImageAspectRatio = imageDimensions.source === originalWorkImage ? imageDimensions.ratio : 1;
    const [weiXinTipMask, setweiXinTipMask] = useState(false);
    
    const posterRef = useRef<HTMLDivElement>(null);
    const pendingSaveRef = useRef(false);

    useEffect(() => {
        if (!visible) {
            setActionSheetVisible(false);
            setPreparedImages(null);
            return;
        }

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [visible]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        let cancelled = false;

        const loadPosterImages = async () => {
            const entries = [
                ...Object.entries(posterImageUrls).filter(([key]) => key !== 'officialBlue' && key !== 'officialGold' && (videoWork || key !== 'videoPlay')),
                ['avatar', avatarUrl],
                ['work', originalWorkImage],
                ...(badgeUrl ? [['badge', badgeUrl]] : []),
            ];
            const images = await Promise.all(entries.map(async ([key, url]) => {
                try {
                    return [key, await convertToBase64(url)];
                } catch {
                    return [key, url];
                }
            }));

            if (!cancelled) {
                setPreparedImages({ key: assetKey, images: Object.fromEntries(images) });
            }
        };

        setPreparedImages(null);
        pendingSaveRef.current = false;
        loadPosterImages();

        return () => {
            cancelled = true;
        };
    }, [visible, assetKey, avatarUrl, originalWorkImage, videoWork, badgeUrl]);

    const workTitle = useMemo(() => {
        const name = workData?.name || '';
        if (!name) return '《未命名作品》';
        if (name.includes('《') && name.includes('》')) return name;
        return `《${name}》`;
    }, [workData]);

    const contestName = useMemo(() => {
        const contestTitle = photographyDetail?.name || 'vivo 社区粉丝摄影大赛';
        const workTitle = workData?.name || '未命名作品';
        return `我正在参加${contestTitle}，快来围观我的作品《${workTitle}》，投我一票！ `;
    }, [photographyDetail, workData]);

    const contestText = useMemo(() => {
        return contestName;
    }, [contestName]);

    const voteGap = useMemo(() => {
        const currentVoteCount = Number(workData?.voteCount || 0);
        const currentTopVoteCount = Math.max(Number(topVoteCount || 0), currentVoteCount);
        if (Number(workData?.ranking) === 1 || currentTopVoteCount <= currentVoteCount) {
            return 0;
        }
        return currentTopVoteCount - currentVoteCount + 1;
    }, [topVoteCount, workData]);

    const voteGapText = useMemo(() => {
        if (Number(workData?.ranking) === 1 || voteGap <= 0) {
            return '当前暂列第1名，请你帮我继续稳住排名';
        }
        return `距超过第1名还差${voteGap}票，请你为我投票`;
    }, [voteGap, workData]);

    const workImageSrc = base64Images.work || originalWorkImage;

    const handleCopyLink = async () => {
        try {
            const shareText = `${contestName}\r\n${shareUrl}\r\n复制本消息打开 vivo 社区 App 即可帮我助力~`;
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(shareText);
            } else {
                copy(shareText);
            }
            Toast.success({ content: '链接复制成功，快去分享好友吧～' });
            setActionSheetVisible(false);
        } catch (error) {
            const shareText = `${contestName}\r\n${shareUrl}\r\n复制本消息打开 vivo 社区 App 即可帮我助力~`;
            copy(shareText);
            Toast.success({ content: '链接复制成功，快去分享好友吧～' });
            setActionSheetVisible(false);
        }
    };

    // 微信覆盖提示
    const isWeixinXml = () => {
        return (
            <div className={styles.opAlertTable} onClick={() => {
                // this.setState({ weiXinTipMask: false } as any);
                setweiXinTipMask(false)
            }}>
                <div className={styles.alertWhite}>
                    <div className={styles.inter}></div>
                    点击右上角选择在默认浏览器中打开
                </div>
            </div>
        );
    }
    // 判断是否是微信
    const isWeixin = () => {
        var ua = navigator.userAgent.toLowerCase();
        return ua.indexOf('micromessenger') !== -1;
    }

    // 等封面等图片解码完成后导出，保留原图细节。
    const handleSaveImage = async () => {
        if(isWeixin()) {
            setweiXinTipMask(true)
            return false;
        }
        if (!posterRef.current) {
            Toast.info({ content: '海报生成失败，请重试' });
            return;
        }

        if (!base64ImagesLoaded) {
            Toast.loading({ content: '图片准备中，请稍候...', duration: 0 });
            pendingSaveRef.current = true;
            return;
        }

        try {
            Toast.loading({ content: '海报生成中···' });
            await Promise.all(Array.from(posterRef.current.querySelectorAll('img')).map(image => image.decode?.().catch(() => {})));
            const posterWidth = posterRef.current.getBoundingClientRect().width;
            if (!posterWidth) throw new Error('Poster is not visible');
            const canvas = await html2canvas(posterRef.current, {
                useCORS: true,
                allowTaint: false,
                // 小屏预览约 320 CSS 像素，导出至少 1280 像素宽，避免保存后被放大。
                scale: Math.max(2, 1280 / posterWidth),
                backgroundColor: null,
            });
            const image = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = image;
            link.download = `photography-share-${workData?.id || Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            Toast.hide();
            Toast.success({ content: '图片已保存' });
            setActionSheetVisible(false);
        } catch (error) {
            Toast.hide();
            Toast.info({ content: '图片保存失败，请重试' });
        }
    };

    // 图片加载完成后，自动执行待处理的保存操作
    useEffect(() => {
        if (base64ImagesLoaded && pendingSaveRef.current) {
            pendingSaveRef.current = false;
            Toast.hide();
            handleSaveImage();
        }
    }, [base64ImagesLoaded]);

    if (!visible || !workData?.id) {
        return null;
    }

    return (
        <div className={styles.sharePosterPopup}>
            <div className={styles.popupMask} onClick={() => onClose && onClose()}></div>
            <div className={styles.popupContent}>
                <div className={styles.posterScrollArea}>
                    <div ref={posterRef} className={styles.posterCapture}>
                        <div className={styles.posterCard}>
                            <img
                                src={base64Images.background || posterImageUrls.background}
                                alt=""
                                className={styles.posterBackground}
                            />
                            <div className={styles.avatarOuterBox}>
                                <div className={styles.avatarBox}>
                                    <Avatar
                                        key={avatarUrl}
                                        avatarUrl={base64Images.avatar || avatarUrl}
                                        userCode={workData?.user?.code}
                                        size={116}
                                        titleMark={workData?.user?.titleMark}
                                        officialSize={32}
                                        showOfficialTag={false}
                                    />
                                    {badgeUrl && (
                                        <img
                                            src={base64Images.badge || badgeUrl}
                                            alt={badgeLabel}
                                            className={officialBadge ? styles.officialIcon : styles.levelIcon}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={styles.posterInner}>
                                <div className={styles.nickname}>{workData?.user?.nickname || 'vivo用户'}</div>
                                <div className={styles.inviteCopy} style={{marginTop:px2rem(16)}}>{'我正在参加vivo社区粉丝摄影大赛'}</div>
                                {/* <div className={styles.voteGapText}>{voteGapText}</div> */}
                                <div className={styles.inviteCopy}>邀请你帮忙投票</div>

                                <div className={styles.workImageBox}>
                                    {/* 直接绘制原图，避免背景图导出时先缩小再放大；按原比例居中裁切。 */}
                                    {workImageSrc && (
                                        <img
                                            key={originalWorkImage}
                                            src={workImageSrc}
                                            className={styles.workImage}
                                            style={{
                                                width: `${Math.max(1, workImageAspectRatio) * 100}%`,
                                                height: `${Math.max(1, 1 / workImageAspectRatio) * 100}%`,
                                            }}
                                            alt={workData?.name || '作品图片'}
                                            onLoad={event => {
                                                // 使用图片实际尺寸计算裁切比例，兼容横图、竖图和视频封面。
                                                const image = event.currentTarget;
                                                if (image.naturalWidth && image.naturalHeight) {
                                                    setImageDimensions({ source: originalWorkImage, ratio: image.naturalWidth / image.naturalHeight });
                                                }
                                            }}
                                        />
                                    )}
                                    {videoWork && <img src={base64Images.videoPlay || posterImageUrls.videoPlay} alt="视频作品" className={styles.videoPlay} />}
                                    {/* live 图标识 */}
                                    {String(workData?.type || '').toLowerCase() === 'live' && (
                                        <img 
                                            src={LIVE_ICON_URL} 
                                            alt="live" 
                                            className={styles.liveIcon}
                                        />
                                    )}
                                </div>

                                <div className={styles.workMetaBox}>
                                    <div className={styles.workNumber}>ID:{workData?.number || '--'} <span className={styles.numGap}></span> {workData?.voteCount || 0}票</div>
                                </div>
                                <div className={styles.workTitle}>{workTitle}</div>
                                <div className={styles.rankTag}>第 {workData?.ranking || '--'} 名</div>

                                <div className={styles.qrcodeBox}>
                                    <QRCode value={shareUrl || window.location.href} size={128} bordered={false} icon={base64Images.qrLogo || posterImageUrls.qrLogo} iconSize={26} />
                                </div>
                                <div className={styles.qrcodeText}>扫码为我助力</div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* 底部操作区：返回上一层作品页面，或打开邀请助力面板。 */}
                <div className={styles.inviteBtnBox}>
                    <button
                        type="button"
                        className={styles.backIcon}
                        aria-label="返回上一页"
                        onClick={onClose}
                    >
                        <img src={`${BASE_IMG_URL}/photography/back4.png`} alt="" />
                    </button>
                    <div className={styles.inviteBtn} onClick={() => setActionSheetVisible(true)}>邀请助力</div>
                </div>
                
            </div>

            {actionSheetVisible && (
                <>
                    <div className={styles.actionSheetMask} onClick={() => setActionSheetVisible(false)}></div>
                    <div className={styles.actionSheet}>
                        <div className={styles.sheetPanel}>
                            <div className={styles.sheetGap}></div>
                            <div className={styles.sheetTitle}>分享好友助力</div>
                            <div className={styles.sheetActionList}>
                                <div className={styles.sheetActionItem} onClick={handleSaveImage}>
                                    <img src={`${BASE_IMG_URL}/photography/icon12.png`} alt=""  className={styles.sheetActionIcon}/>
                                    <div className={styles.sheetActionText}>保存图片</div>
                                </div>
                                <div className={styles.sheetActionItem} onClick={handleCopyLink}>
                                    <img src={`${BASE_IMG_URL}/photography/icon13.png`} alt=""  className={styles.sheetActionIcon}/>
                                    <div className={styles.sheetActionText}>复制链接</div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.sheetDividerBar}></div>
                        <div className={styles.cancelBtn} onClick={() => setActionSheetVisible(false)}>取消</div>
                    </div>
                </>
            )}
            {/* 微信内提示弹窗 */}
            {weiXinTipMask && isWeixinXml()}
        </div>
    );
};

export default SharePosterPopup;
